import { membershipApi } from 'app/src/services/welfareportal'
import { ErrorMessage } from 'app/src/shared/components'
import { queryClient } from 'app/src/shared/helpers'
import { useWebAppContext } from 'app/src/shared/hooks'
import { useFormik } from 'formik'
import { isEqual } from 'lodash'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputText } from 'primereact/inputtext'
import { useCallback, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { object, type AnyObject, type StringSchema } from 'yup'

type PropertyFormatterFn = { format: (str: string | undefined) => string | undefined }

export type UserPropertyFormProps = {
  membershipId: string
  propertyName: 'userEmail' | 'userIban'
  propertyDisplayName: string
  propertyValue: string | undefined
  yupCondition: StringSchema<string, AnyObject, undefined, ''>
  propertyFormatter?: PropertyFormatterFn
}

export function UserPropertyForm(props: UserPropertyFormProps & { onClose: () => void }): ReactElement {
  const { t } = useTranslation('userInfo', { keyPrefix: 'UserInfo.UserPropertyForm' })
  const context = useWebAppContext()
  const membershipMutation = membershipApi.useMembershipPatch({
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryOperationId] = query.queryKey
          return queryOperationId === 'membership-api.membershipGet'
        },
      })
      props.onClose()
    },
    onError: (error: membershipApi.MembershipPatchError) => {
      if (error.status === 400) {
        context.showToastMessage({ severity: 'error', content: t('Errors')[props.propertyName].BadRequestError })
      } else context.showToastMessage({ severity: 'error', content: t('Errors')[props.propertyName].GenericSaveError })

      context.logger?.error('Error Patching Membership Property', { propertyName: props.propertyName, error })
    },
  })

  const formik = useFormik({
    initialValues: {
      [props.propertyName]: props.propertyFormatter?.format
        ? props.propertyFormatter.format(props.propertyValue)
        : props.propertyValue,
    },
    validationSchema: object().shape({
      [props.propertyName]: props.yupCondition,
    }),
    enableReinitialize: true,
    onSubmit: (values: { [x: string]: string | undefined }) => {
      updateUserProperty(values)
    },
  })

  const updateUserProperty = useCallback(
    async (values: { [x: string]: string | undefined }) => {
      if (!isEqual(props.propertyValue, values[props.propertyName])) {
        await membershipMutation.mutateAsync({
          pathParams: {
            membershipId: props.membershipId,
          },
          body: {
            [props.propertyName === 'userEmail' ? 'email' : props.propertyName === 'userIban' ? 'iban' : '']: props
              .propertyFormatter?.format
              ? props.propertyFormatter?.format(values[props.propertyName])
              : values[props.propertyName],
            userId: context.userInfo.userId,
          },
        })
      } else props.onClose()
    },
    [membershipMutation, props, context],
  )
  return (
    <Card
      className='flex-1'
      title={props.propertyDisplayName}
      footer={
        <div className='text-right'>
          <Button
            outlined
            rounded
            className='m-1 min-w-32 items-center justify-center'
            onClick={(e) => {
              formik.handleReset(e)
              props.onClose()
            }}
          >
            {t('Cancel')}
          </Button>
          <Button rounded className='m-1 min-w-32 items-center justify-center' onClick={() => formik.handleSubmit()}>
            {t('Update')}
          </Button>
        </div>
      }
    >
      <div>
        <InputText
          value={formik.values[props.propertyName]}
          autoFocus
          onChange={(evt) => {
            formik.setFieldValue(
              props.propertyName,
              props.propertyFormatter?.format
                ? props.propertyFormatter?.format(evt.currentTarget.value)
                : evt.currentTarget.value,
            )
          }}
          className='w-full min-w-[250px] rounded-none border-x-[0px] border-b-[1px] border-t-[0px] border-solid border-[#605e5c]'
          id={props.propertyName}
          invalid={typeof formik.errors[props.propertyName] !== 'undefined'}
          aria-describedby={props.propertyName}
          placeholder={t('InputPlaceholder')}
          maxLength={100}
        />
        <ErrorMessage errorMessage={formik.errors[props.propertyName]} />
      </div>
    </Card>
  )
}
